import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { Stack, IconButton, Chip, useMediaQuery, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { AppVersion } from "../../types/appVersion";
import {
  useGetAppVersionsQuery,
  useEnabledAppVersionMutation,
  useDisabledAppVersionMutation,
} from "../../services/app-version.service";
import ModalAppUpdateForm from "./ModalAppUpdateForm";
import dayjs from "dayjs";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";

const AppUpdatesPage = () => {
  useTitle("App Updates");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formModal, setFormModal] = React.useState<{
    open: boolean;
    data: AppVersion | null;
  }>({
    open: false,
    data: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const { data, isLoading, isFetching } = useGetAppVersionsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doEnabled, { isLoading: enabling }] = useEnabledAppVersionMutation();
  const [doDisabled, { isLoading: disabling }] =
    useDisabledAppVersionMutation();

  const handleEnabledRow = React.useCallback(async (row: GridRowModel<AppVersion>) => {
    try {
      await doEnabled(row.id).unwrap();
      toast.success("Successfully updated");
    } catch (err) { }
  }, [doEnabled]);

  const handleEditRow = React.useCallback((row: GridRowModel<AppVersion>) => {
    setFormModal({ open: true, data: row });
  }, []);

  const handleDisabledRow = React.useCallback(async (row: GridRowModel<AppVersion>) => {
    try {
      await doDisabled(row.id).unwrap();
      toast.success("Successfully updated");
    } catch (err) { }
  }, [doDisabled]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "app_id",
        headerName: "App ID",
        headerAlign: "center",
        flex: 1,
        filterable: true,
        sortable: true,
        align: "center",
      },
      {
        field: "platform",
        headerName: "Platform",
        headerAlign: "center",
        flex: 1,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        sortable: true,
        align: "center",
      },
      {
        field: "version",
        headerName: "Version",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, AppVersion>) =>
          `${params.row.version} (${params.row.version_name})`,
      },
      {
        field: "mandatory",
        headerName: "Is Mandatory",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 50,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, AppVersion>) =>
          `${params.row.mandatory ? "Yes" : "No"}`,
      },
      // {
      //   field: "release_notes",
      //   headerName: "Release Notes",
      //   minWidth: 200,
      //   sortable: false,
      //   filterable: false,
      // },
      {
        field: "enabled",
        headerName: "Status",
        headerAlign: "center",
        flex: 1,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, AppVersion>) => (
          <Stack direction={"row"}>
            <Chip
              size="small"
              label={params.row.enabled ? "ENABLED" : "DISABLED"}
              color={params.row.enabled ? "success" : "error"}
              sx={{ fontSize: 12 }}
            />
          </Stack>
        ),
      },
      {
        field: "created_at",
        headerName: "Created At",
        headerAlign: "center",
        minWidth: isMobile ? 120 : 200,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, AppVersion>) =>
          `${dayjs(params.row.created_at).format("MMM DD, YYYY hh:mm A")}`,
      },
      {
        field: "control",
        headerName: "Actions",
        headerAlign: "center",
        hide: false,
        width: 180,
        minWidth: 180,
        maxWidth: 180,
        flex: 0,
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, AppVersion>) => (
          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
            {params.row.enabled ? (
              <Button
                variant="outlined"
                onClick={() => handleDisabledRow(params.row)}
                size="small"
                disabled={disabling}
                sx={{
                  color: "#d32f2f",
                  borderColor: "#d32f2f",
                  "&:hover": {
                    borderColor: "#d32f2f",
                    backgroundColor: "rgba(211, 47, 47, 0.1)",
                  },
                }}
              >
                Disable
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={() => handleEnabledRow(params.row)}
                size="small"
                disabled={enabling}
                sx={{
                  color: "#F4A259",
                  borderColor: "#F4A259",
                  "&:hover": {
                    borderColor: "#F4A259",
                    backgroundColor: "rgba(244, 162, 89, 0.1)",
                  },
                }}
              >
                Enable
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => handleEditRow(params.row)}
              sx={{
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.1)",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
        disableExport: true,
      },
    ],
    [handleEnabledRow, handleDisabledRow, handleEditRow, enabling, disabling, isMobile]
  );

  const handleCreate = React.useCallback(() => {
    setFormModal({ open: true, data: null });
  }, []);

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <>
      <CardBase
        title="App Updates"
        useGlassCard={false}
        sx={{ height: isMobile ? "70vh" : "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{ minWidth: isMobile ? "100%" : 250 }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                width: isMobile ? "100%" : "auto",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              {!!query && (
                <Button
                  variant="text"
                  onClick={() => setQuery("")}
                  size="small"
                  sx={{
                    color: "#F4A259",
                    "&:hover": {
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                    },
                  }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleCreate}
                startIcon={<AddIcon />}
                size="small"
                sx={{
                  backgroundColor: "#F4A259",
                  color: "#ffffff",
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(244, 162, 89, 0.3)",
                  "&:hover": {
                    backgroundColor: "#E8944A",
                    boxShadow: "0 4px 12px rgba(244, 162, 89, 0.4)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isMobile ? "Add" : "Add App Version"}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          height={isMobile ? "calc(70vh - 120px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              py: isMobile ? 1 : 2,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <ModalAppUpdateForm
        open={formModal.open}
        onClose={() => setFormModal({ open: false, data: null })}
        data={formModal.data}
      />
    </>
  );
};
export default AppUpdatesPage;
